import './emote.css';
import { t } from './i18n';
import { EMOTES, EMOTES_BY_ID, WHEEL_SLOTS, loadWheel, saveWheel, type EmoteDef } from '../emotes/catalog';
import { PREMIUM_EMOTE_IDS } from '../store/catalog';

/**
 * Radial emote wheel. Hold E to open it centred on screen; a line runs from the
 * centre to the cursor and the slot in that direction highlights — release E (or
 * click a slot) to fire it. The ring holds the 8 configured emotes plus a Configure
 * slot that opens the assignment view. Tapping E (open, no pointing) leaves it open
 * so you can click instead; pressing E again closes it.
 */
export interface EmoteWheelCallbacks {
  onEmote(id: string): void;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  return e;
}

export class EmoteWheel {
  private readonly overlay = el('div', 'emote-overlay');
  private readonly wheelView = el('div', 'emote-wheel');
  private readonly configView = el('div', 'emote-config hud-panel');
  private readonly ring = el('div', 'emote-ring');
  private readonly line = el('div', 'emote-line');
  private readonly configBtn = el('button', 'emote-cfg-btn interactive');
  private wheel: string[] = loadWheel();
  private slots: string[] = []; // ring contents: the 8 emotes (config is a separate corner button)
  private slotEls: HTMLButtonElement[] = [];
  private radius = 130;
  private sel = -1; // currently pointed-at slot, or -1
  private open = false;
  private configSlot = 0; // slot being edited in the config view
  // Premium emotes (see src/store/catalog.ts) bought from the item store; a
  // premium emote not in this set can't be assigned to a wheel slot yet.
  private owned = new Set<string>();

  constructor(root: HTMLElement, private cb: EmoteWheelCallbacks) {
    this.overlay.style.display = 'none';
    this.overlay.onclick = (e) => {
      if (e.target === this.overlay) this.close(); // click empty backdrop = close
    };
    this.overlay.onmousemove = (e) => this.onMove(e.clientX, e.clientY);

    const dot = el('div', 'emote-dot'); // centre origin of the pointer line
    this.wheelView.append(this.line, this.ring, dot);
    // Configure: a standalone button in the bottom-right corner (not on the ring, so
    // it never gets caught by the line's emote selection).
    this.configBtn.innerHTML = `⚙ ${t('emote.configure')}`;
    this.configBtn.onclick = () => this.showConfig();
    this.overlay.append(this.wheelView, this.configView, this.configBtn);
    root.appendChild(this.overlay);
    this.buildRing();
    this.buildConfig();
  }

  /** Lay the 8 emote slots out around the ring, slot 0 at top, clockwise. */
  private buildRing(): void {
    this.slots = [...this.wheel];
    this.ring.replaceChildren();
    this.slotEls = [];
    const n = this.slots.length;
    for (let i = 0; i < n; i++) {
      const def = EMOTES_BY_ID.get(this.slots[i]);
      const angle = (-90 + (360 / n) * i) * (Math.PI / 180);
      const x = Math.cos(angle) * this.radius;
      const y = Math.sin(angle) * this.radius;
      const slot = el('button', 'emote-slot interactive');
      slot.style.transform = `translate(${x}px, ${y}px)`;
      slot.innerHTML = `<span class="emote-glyph">${def?.glyph ?? '?'}</span><span class="emote-label">${def?.label ?? ''}</span>`;
      slot.onclick = () => this.commit(i);
      this.ring.appendChild(slot);
      this.slotEls.push(slot);
    }
  }

  /** Mouse moved while open: aim the line at the cursor and highlight the nearest slot. */
  private onMove(mx: number, my: number): void {
    if (!this.open || this.configuring) return;
    const cx = window.innerWidth / 2;
    const cy = window.innerHeight / 2;
    const dx = mx - cx;
    const dy = my - cy;
    const dist = Math.hypot(dx, dy);
    if (dist < 34) {
      // inside the dead-zone: no selection, hide the line
      this.line.style.opacity = '0';
      this.setSel(-1);
      return;
    }
    this.line.style.opacity = '1';
    this.line.style.width = `${dist}px`;
    this.line.style.transform = `rotate(${Math.atan2(dy, dx)}rad)`;
    // nearest slot by angle
    const n = this.slots.length;
    const a = (Math.atan2(dy, dx) * 180) / Math.PI; // -180..180, 0 = +x
    let best = 0;
    let bestD = 1e9;
    for (let i = 0; i < n; i++) {
      const slotA = -90 + (360 / n) * i; // matches buildRing
      const d = Math.abs(((a - slotA + 540) % 360) - 180); // angular distance 0..180
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    this.setSel(best);
  }

  private setSel(i: number): void {
    if (i === this.sel) return;
    if (this.sel >= 0) this.slotEls[this.sel]?.classList.remove('sel');
    this.sel = i;
    if (i >= 0) this.slotEls[i]?.classList.add('sel');
  }

  /** Fire the emote in slot i (and close the wheel). */
  private commit(i: number): void {
    if (i < 0 || i >= this.slots.length) return;
    const id = this.slots[i];
    this.close();
    this.cb.onEmote(id);
  }

  /** True while the Configure view (not the ring) is showing. */
  private get configuring(): boolean {
    return this.configView.style.display === 'flex';
  }

  /** E released: fire the pointed-at slot; if none (tap, no pointing) stay open. */
  onKeyRelease(): void {
    // While configuring, releasing E must not fire/close — otherwise a slot that got
    // highlighted while mousing over to the ⚙ button would fire and shut the overlay.
    if (!this.open || this.configuring) return;
    if (this.sel >= 0) this.commit(this.sel);
  }

  // --- configure view ---------------------------------------------------------
  private buildConfig(): void {
    this.configView.replaceChildren();
    const title = el('div', 'emote-cfg-title');
    title.textContent = t('emote.configure');
    const hint = el('div', 'emote-cfg-hint');
    hint.textContent = t('emote.configureHint');

    const slots = el('div', 'emote-cfg-slots');
    this.wheel.forEach((id, i) => {
      const def = EMOTES_BY_ID.get(id);
      const b = el('button', 'emote-cfg-slot interactive');
      if (i === this.configSlot) b.classList.add('sel');
      b.innerHTML = `<span class="emote-glyph">${def?.glyph ?? '?'}</span>`;
      b.title = `${i + 1}`;
      b.onclick = () => {
        this.configSlot = i;
        this.buildConfig();
      };
      slots.appendChild(b);
    });

    const cat = el('div', 'emote-cfg-catalog');
    for (const def of EMOTES) {
      const locked = PREMIUM_EMOTE_IDS.has(def.id) && !this.owned.has(def.id);
      const b = el('button', 'emote-cfg-item interactive');
      if (locked) b.classList.add('locked');
      const tag = def.kind === 'sound' ? '🔊' : '';
      const lockTag = locked ? '🔒' : '';
      b.innerHTML = `<span class="emote-glyph">${def.glyph}</span><span class="emote-label">${def.label} ${tag}${lockTag}</span>`;
      if (locked) {
        b.disabled = true;
        b.title = t('emote.locked');
      } else {
        b.onclick = () => this.assign(def);
      }
      cat.appendChild(b);
    }

    const done = el('button', 'emote-cfg-done interactive');
    done.textContent = t('emote.done');
    done.onclick = () => this.showWheel();

    this.configView.append(title, hint, slots, cat, done);
  }

  private assign(def: EmoteDef): void {
    this.wheel[this.configSlot] = def.id;
    saveWheel(this.wheel);
    this.buildRing();
    this.configSlot = (this.configSlot + 1) % WHEEL_SLOTS; // advance for quick editing
    this.buildConfig();
  }

  private showConfig(): void {
    this.configSlot = 0;
    this.setSel(-1);
    this.line.style.opacity = '0';
    this.buildConfig();
    this.wheelView.style.display = 'none';
    this.configBtn.style.display = 'none';
    this.configView.style.display = 'flex';
  }

  private showWheel(): void {
    this.setSel(-1); // drop any highlight left over from before configuring
    this.line.style.opacity = '0';
    this.configView.style.display = 'none';
    this.wheelView.style.display = '';
    this.configBtn.style.display = '';
  }

  // --- open / close -----------------------------------------------------------
  openWheel(): void {
    this.open = true;
    this.setSel(-1);
    this.line.style.opacity = '0';
    this.showWheel();
    this.overlay.style.display = 'flex';
  }

  close(): void {
    this.open = false;
    this.setSel(-1);
    this.overlay.style.display = 'none';
  }

  isOpen(): boolean {
    return this.open;
  }

  /** Update which premium emotes are unlocked (owned via the item store). */
  setOwned(owned: Set<string>): void {
    this.owned = owned;
    if (this.configuring) this.buildConfig();
  }
}
